import { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Moon } from "lucide-react";
import { WindowProvider } from "@/os/window/WindowManager";
import { useWindows } from "@/os/window/context";
import { WindowFrame } from "@/os/window/WindowFrame";
import { DesktopIcon } from "./DesktopIcon";
import { Taskbar } from "./Taskbar";
import { DESKTOP_ICONS } from "@/os/window/registry";
import { useSettings } from "@/os/state/settings";
import { useToast } from "@/os/state/toast";
import { useTheme } from "@/os/state/theme";
import { DoodleArrow, DoodleStar, DoodleSquiggle, TabRain } from "@/components/decor";
import { STICKY_BASE, STICKY_CHAOS, STICKY_OVERTHINK } from "@/data/misc";
import { useAchievements } from "@/os/state/achievements";
import { awayLabel, visitInfo } from "@/os/state/session";

const LOAD_LINES = ["reallocating attention ...", "compressing side quests ...", "buffering excuses ...", "almost there. probably."];

export default function Desktop({ initialOpen }: { initialOpen?: string }) {
    return (
        <WindowProvider initialOpen={initialOpen}>
            <DesktopInner />
        </WindowProvider>
    );
}

function DesktopInner() {
    const { sliders, modes } = useSettings();
    const { theme } = useTheme();
    const { trackEgg } = useAchievements();
    const { windows, loading, constraintsRef, open, skipLoading } = useWindows();
    const { push } = useToast();
    const [bedtimeDismissed, setBedtimeDismissed] = useState(false);
    const perfOpened = useRef(false);
    const [loadLine, setLoadLine] = useState(0);
    const [knock, setKnock] = useState(0);

    useEffect(() => {
        let buffer = "";
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const onKey = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.key.length !== 1) return;
            buffer = (buffer + e.key.toLowerCase()).slice(-10);
            if (buffer.endsWith("krithi")) {
                buffer = "";
                setKnock((k) => k + 1);
                trackEgg("egg_secret_knock");
                push(
                    "system",
                    "secret knock accepted",
                    reduce ? "37 more tabs incoming. (motion reduced — they arrive politely.)" : "37 more tabs incoming. brace the wallpaper.",
                );
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [push, trackEgg]);

    const greeted = useRef(false);
    useEffect(() => {
        if (greeted.current) return;
        greeted.current = true;
        const visit = visitInfo();
        if (!visit.returning) {
            push("system", "krithi.exe is running", "37 tabs restored. welcome to the desktop.");
            return;
        }
        const mood = modes.chaotic
            ? "chaos mode is still on, exactly as you left it."
            : modes.bedtime
              ? "bedtime conflict is still unresolved, exactly as you left it."
              : modes.impatient
                ? "patience is still low, exactly as you left it."
                : `curiosity ${sliders.curiosity} · overthinking ${sliders.overthinking} — restored.`;
        push("system", `welcome back · last seen ${awayLabel(visit.daysAway)}`, mood);
    }, [push, modes, sliders.curiosity, sliders.overthinking]);

    useEffect(() => {
        if (modes.impatient && !perfOpened.current) {
            perfOpened.current = true;
            open("performance");
        }
        if (!modes.impatient) perfOpened.current = false;
    }, [modes.impatient, open]);

    useEffect(() => {
        if (!loading) return;
        setLoadLine(0);
        const t = setInterval(() => setLoadLine((v) => (v + 1) % LOAD_LINES.length), 620);
        return () => clearInterval(t);
    }, [loading]);

    const notes = [
        ...STICKY_BASE,
        ...(sliders.overthinking >= 70 ? STICKY_OVERTHINK : []),
        ...(modes.chaotic ? STICKY_CHAOS : []),
    ];

    return (
        <div ref={constraintsRef} className={`os-desktop theme-${theme} ${modes.bedtime ? "bedtime" : ""}`} data-testid="os-desktop">
            <div className="grain" aria-hidden />
            <DoodleSquiggle className="doodle left-[45%] top-8 hidden lg:block" />
            <DoodleArrow className="doodle left-[36%] top-[38%] hidden lg:block" />
            <DoodleStar />

            <main
                className="os-scroll relative z-10 h-full w-full overflow-y-auto px-4 pb-36 pt-6 md:px-8 lg:px-12"
                data-testid="desktop-main"
                onDoubleClick={(e) => {
                    if (e.target === e.currentTarget) {
                        trackEgg("egg_wallpaper_cleaner");
                        push("system", "the empty desktop", "cleaner here, isn't it? the tabs heard you and refused to leave.");
                    }
                }}
            >
                <section className="max-w-2xl" data-testid="hero">
                    <p className="font-mono text-[11px] text-inkfaint">
                        KRITHI.OS // user: krithi · session: hiring_challenge
                    </p>
                    <h1 className="mt-3 font-display text-[clamp(26px,4.2vw,44px)] font-extrabold leading-[1.12] tracking-tight text-ink">
                        Hi I'm Krithi. My brain has too many tabs open so I turn them into a website.
                    </h1>
                    <p className="mt-3 text-sm text-inksoft md:text-base">
                        Applied AI engineer. Document pipelines by day, saxophone by night. Occasional victim of my own curiosity.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3">
                        <button data-testid="hero-open-experiments" className="btn btn-blue btn-sm" onClick={() => open("experiments")}>
                            open experiments
                        </button>
                        <button data-testid="hero-open-resume" className="btn btn-sm" onClick={() => open("resume_pdf")}>
                            open resume
                        </button>
                    </div>
                    <p className="mt-4 font-mono text-[10.5px] text-inkfaint">
                        click an icon. windows open, close, minimize and stack — like your brain, but tidier.
                    </p>
                </section>

                <div className="os-icons mt-6 max-w-[640px]" data-testid="desktop-icons">
                    {DESKTOP_ICONS.map((id) => (
                        <DesktopIcon key={id} id={id} />
                    ))}
                </div>
            </main>

            <AnimatePresence>
                {windows.map((w) => (
                    <WindowFrame key={w.id} win={w} />
                ))}
            </AnimatePresence>

            {notes.map((n, i) => (
                <div
                    key={n.text}
                    className={`sticky-note wall-art ${n.pos}`}
                    style={{ background: n.color }}
                    data-testid={i < STICKY_BASE.length ? "sticky-base" : "sticky-extra"}
                >
                    {n.text}
                </div>
            ))}

            {modes.bedtime && !bedtimeDismissed && (
                <BedtimeBanner
                    onOneMore={() => {
                        open("tabs");
                        push("system", "one more tab", "it's never one.");
                    }}
                    onFive={() => push("system", "five more minutes", "the OS pretends to believe you.")}
                    onClose={() => setBedtimeDismissed(true)}
                />
            )}

            {loading && (
                <div className="fixed inset-0 z-[4600] grid place-items-center bg-ink/25 p-4 backdrop-blur-sm" data-testid="loading-overlay">
                    <div className="pop-in w-[min(92vw,380px)] rounded-2xl border-2 border-ink bg-paper p-5 text-center shadow-[6px_6px_0_#1C1917]">
                        <p className="font-mono text-[11px] text-inkfaint">simulated loading screen</p>
                        <p className="mt-2 font-mono text-sm text-ink" data-testid="loading-line">
                            {LOAD_LINES[loadLine]}
                        </p>
                        <div className="mx-auto mt-4 h-2 w-full max-w-[240px] overflow-hidden rounded-full border border-ink/20 bg-sandsoft">
                            <div className="h-full w-1/3 rounded-full bg-coral" style={{ animation: "floaty 1.2s ease-in-out infinite" }} />
                        </div>
                        <p className="mt-3 font-mono text-[10px] text-inkfaint">
                            (tolerance for slow apps is low. the OS noticed.)
                        </p>
                        <div className="mt-3 flex flex-col gap-2">
                            <button
                                data-testid="loading-investigate"
                                className="btn btn-coral btn-sm"
                                onClick={() => {
                                    skipLoading();
                                    open("bugs_i_fixed");
                                }}
                            >
                                Investigate the case
                            </button>
                            <button data-testid="loading-wait" className="btn btn-ghost btn-sm" onClick={skipLoading}>
                                wait it out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <TabRain trigger={knock} />
            <Taskbar />
        </div>
    );
}

function BedtimeBanner(props: { onOneMore: () => void; onFive: () => void; onClose: () => void }) {
    return (
        <div
            className="pop-in fixed left-1/2 top-4 z-[4400] w-[min(94vw,480px)] -translate-x-1/2 rounded-2xl border-2 border-lav bg-lavsoft/95 p-4 shadow-[5px_5px_0_#1C1917]"
            data-testid="bedtime-banner"
        >
            <div className="flex items-start gap-2">
                <Moon size={16} className="mt-0.5 flex-none text-lav" />
                <div className="flex-1">
                    <p className="font-display text-sm font-extrabold">bedtime conflict</p>
                    <p className="mt-1 text-[12.5px] text-inksoft">
                        curiosity demands one more tab. sleep optimization demands rest. they've been arguing for a while.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button data-testid="bedtime-one-more" className="btn btn-sm" onClick={props.onOneMore}>
                            one more tab
                        </button>
                        <button data-testid="bedtime-five-minutes" className="btn btn-sm" onClick={props.onFive}>
                            fine, five more minutes
                        </button>
                    </div>
                </div>
                <button data-testid="bedtime-dismiss" aria-label="Dismiss" className="text-inkfaint hover:text-ink" onClick={props.onClose}>
                    ×
                </button>
            </div>
        </div>
    );
}
